import React, { FC, useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  TooltipItem,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import HeaderBtn from '../../headerBtn/headerBtn';

import { ReactComponent as SettingIcon } from '../../../assets/icons/settings.svg';
import { ReactComponent as IncreaseIcon } from '../../../assets/icons/increase.svg';
import { ReactComponent as ReduceIcon } from '../../../assets/icons/reduce.svg';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/chevron.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filter.svg';
import { ReactComponent as ActiveFiltersIcon } from '../../../assets/icons/filter_active.svg';

import { AppDispatch } from '../../../redux/reducer/store';

import { actionsAnalyticFacing } from '../../../redux/action/charts/actionCharts';
import {
  reducerChangeDataCharts,
  reducerEditTypeChart,
} from '../../../redux/reducer/charts/reducer/reducerCharts';

import ChartComponent from '../../custom/chartComponet/chartComponent';
import ChartLoader from '../../custom/chartLoader/chartLoader';
import ScrollingLegend from '../../custom/legend/legend';

import {
  createFormatDateFunction,
  filteredZeroData,
  reverseChartsList,
  lineChartList,
  monthOptions,
  defaultFilter,
  normList,
  tickData,
  getColorOptions,
} from '../../../utils/chartsData';
import {
  IChartOptions,
  IFiltersCharts,
  IContextOptions,
} from '../../../types/chartsInterface';
import { formatDateForMapReport } from '../../../utils/formatDate';

import '../charts.scss';

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels,
  PointElement,
  LineElement,
);

const typesCharts = [
  { value: 'bar', label: 'Гистограмма' },
  { value: 'barStacked', label: 'Гистограмма с накоплением' },
  { value: 'barStackedNorm', label: 'Нормированная гистограмма с накоплением' },
  { value: 'lineBar', label: 'Линейчатая' },
  { value: 'lineBarStacked', label: 'Линейчатая с накоплением' },
  { value: 'lineBarNorm', label: 'Нормированная линейчатая с накоплением' },
  { value: 'lineChart', label: 'График' },
  { value: 'lineChartStacked', label: 'График с накоплением' },
  {
    value: 'lineChartStackedNorm',
    label: 'Нормированный график с накоплением',
  },
];

const FacingChart: FC<IChartOptions> = ({ id, copyChart, isCopy, pageId }) => {
  const dispatch: any = useDispatch<AppDispatch>();
  const listRef = useRef<HTMLDivElement | null>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const scrollStep = 21.5;

  const {
    pending,
    desktopData,
    groupFilters,
    settingCharts,
    desktopInputValues,
  } = useSelector((state: any) => state.charts?.chartsList);
  const { darkTheme } = useSelector((state: any) => state.theme.theme);

  const ticks = {
    ...tickData,
    color: darkTheme.isDarkTheme ? 'white' : 'black',
  };

  const colorList = getColorOptions(settingCharts?.facing);
  const index =
    desktopData[pageId || 0]?.find((el: { id: number }) => el.id === id) || {};
  const facingChartsFilters =
    desktopInputValues?.[Number(pageId) || 0]?.[id] || null;

  const {
    type,
    labels,
    groupped,
    periodList,
    dataPrecent,
    periodListOfDate,
    dataFacingCharts,
  } = index;

  const {
    filters,
    areasOptions,
    chainsOptions,
    regionsOptions,
    projectOptions,
    contractsOptions,
  } = useSelector((state: any) => state.userReports.userReports);

  const [isIncreasedSize, setIsIncreasedSize] = useState<boolean>(false);
  const [isOpenSetting, setIsOpenSetting] = useState<boolean>(false);
  const [isOpenSectionCharts, setIsOpenSectionCharts] =
    useState<boolean>(false);
  const [isListRightPosition, setIsListRightPosition] =
    useState<boolean>(false);
  const [isListRightFiltersPosition, setIsListRightFiltersPosition] =
    useState<boolean>(false);
  const [isChangeData, setIsChangeData] = useState<boolean>();
  const [isloader, setIsloader] = useState<boolean>(true);
  const [isVisiblelabel, setIsVisiblelabel] = useState<any[]>([]);
  const [isHiddenLabel, setIsHiddenLabel] = useState<string[]>([]);
  const [isPeriod, setIsPeriod] = useState<string[]>([]);
  const [isData, setIsData] = useState([]);

  const defaultStack: string[] = ['bar', 'lineBar', 'lineChart'];

  const grouppedName =
    groupped === 'subgroup'
      ? 'подгруппам'
      : groupped === 'group'
      ? 'группам'
      : groupped === 'category'
      ? 'подкатегориям'
      : 'категориям';

  const axisX = [
    {
      formatDate: (data: string) => {
        return formatDateForMapReport(data);
      },
    },
    {
      formatDate: createFormatDateFunction(contractsOptions),
    },
    {
      formatDate: createFormatDateFunction(projectOptions),
    },
    {
      formatDate: createFormatDateFunction(chainsOptions),
    },
    {
      formatDate: createFormatDateFunction(areasOptions),
    },
    {
      formatDate: createFormatDateFunction(regionsOptions),
    },
    {
      formatDate: createFormatDateFunction(monthOptions),
    },
    {
      formatDate: createFormatDateFunction(groupFilters?.sku),
    },
  ];

  const data = {
    labels: periodList ? isPeriod : [],
    datasets: isData,
  };

  const optionsForBar = {
    responsive: true,
    indexAxis: type === 'lineBar' ? ('y' as const) : ('x' as const),
    layout: {
      padding: {
        top: type === 'lineBar' ? 0 : 30,
      },
    },
    scales: {
      x: {
        stacked: !defaultStack.includes(type),
        ticks: ticks,
        grid: {
          color: darkTheme.isDarkTheme ? 'white' : 'black',
        },
      },
      y: {
        stacked: !defaultStack.includes(type),
        ticks: ticks,
        grid: {
          color: darkTheme.isDarkTheme ? 'white' : 'black',
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem: TooltipItem<typeof type>) {
            const label = tooltipItem.dataset.label;
            const dataset = tooltipItem.formattedValue;
            return `${label}: ${dataset}`;
          },
          title: function (tooltipItem: TooltipItem<typeof type>[]) {
            return `${tooltipItem[0].label}`;
          },
        },
      },
      datalabels: {
        color: '#000',
        align: 'center' as const,
        anchor: 'center' as const,
        formatter: (value: number, context: IContextOptions) => {
          if (value > 0 && !lineChartList.includes(type)) {
            const { chart } = context;
            const barWidth = chart.getDatasetMeta(context.datasetIndex).data[
              context.dataIndex
            ].$animations.width._to;
            const barHeigth = chart.getDatasetMeta(context.datasetIndex).data[
              context.dataIndex
            ].$animations.height._to;
            const widthSize = value.toString().length * 10;

            if (barHeigth > 13 && barWidth >= widthSize) {
              return value.toLocaleString('ru-RU');
            } else {
              return '';
            }
          } else {
            return '';
          }
        },
      },
    },
  };

  const optionsForStacked = {
    responsive: true,
    indexAxis: type === 'lineBarStacked' ? ('y' as const) : ('x' as const),
    layout: {
      padding: {
        top: 30,
      },
    },
    scales: {
      x: {
        stacked: !defaultStack.includes(type),
        ticks: ticks,
        grid: {
          color: darkTheme.color,
        },
      },
      y: {
        stacked: !defaultStack.includes(type),
        ticks: ticks,
        grid: {
          color: darkTheme.color,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem: TooltipItem<typeof type>) {
            const label = tooltipItem.dataset.label;
            const dataset = tooltipItem.formattedValue;
            return `${label}: ${dataset}`;
          },
          title: function (tooltipItem: TooltipItem<typeof type>[]) {
            return `${tooltipItem[0].label}`;
          },
        },
      },
      datalabels: {
        color: '#000',
        align: 'center' as const,
        anchor: 'center' as const,
        formatter: (value: number, context: IContextOptions) => {
          if (value > 0 && !lineChartList.includes(type)) {
            const { chart } = context;
            const barWidth = chart.getDatasetMeta(context.datasetIndex).data[
              context.dataIndex
            ].$animations.width._to;
            const barHeigth = chart.getDatasetMeta(context.datasetIndex).data[
              context.dataIndex
            ].$animations.height._to;
            const widthSize = value.toString().length * 10;

            if (barHeigth > 13 && barWidth >= widthSize) {
              return value.toLocaleString('ru-RU');
            } else {
              return '';
            }
          } else {
            return '';
          }
        },
      },
    },
  };

  const optionsForNormalise = {
    responsive: true,
    indexAxis: type === 'lineBarNorm' ? ('y' as const) : ('x' as const),
    layout: {
      padding: {
        top: 30,
      },
    },
    scales: {
      x: {
        stacked: !defaultStack.includes(type),
        max: type === 'lineBarNorm' ? 100 : undefined,
        ticks: ticks,
        grid: {
          color: darkTheme.isDarkTheme ? 'white' : 'black',
        },
      },
      y: {
        stacked: !defaultStack.includes(type),
        max: type === 'barStackedNorm' ? 100 : undefined,
        ticks: ticks,
        grid: {
          color: darkTheme.isDarkTheme ? 'white' : 'black',
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem: TooltipItem<typeof type>) {
            const label = tooltipItem.dataset.label;
            const dataset = tooltipItem.formattedValue;
            return `${label}: ${dataset}%`;
          },
          title: function (tooltipItem: TooltipItem<typeof type>[]) {
            return `${tooltipItem[0].label}`;
          },
        },
      },
      legend: {
        display: false,
      },
      datalabels: {
        color: '#000',
        align: 'center' as const,
        anchor: 'center' as const,
        formatter: (value: number, context: IContextOptions) => {
          if (value > 0 && !lineChartList.includes(type)) {
            const { chart } = context;
            const barWidth = chart.getDatasetMeta(context.datasetIndex).data[
              context.dataIndex
            ].$animations.width._to;
            const barHeigth = chart.getDatasetMeta(context.datasetIndex).data[
              context.dataIndex
            ].$animations.height._to;
            const widthSize = value.toFixed().length * 10;

            if (barHeigth > 13 && barWidth >= widthSize) {
              return value >= 3 ? `${Math.floor(value)}%` : '';
            } else {
              return '';
            }
          } else {
            return '';
          }
        },
      },
    },
  };

  const actualOptionsChart = normList.includes(type)
    ? optionsForNormalise
    : defaultStack.includes(type)
    ? optionsForBar
    : optionsForStacked;

  const getChangeData = (data: IFiltersCharts) => {
    const changes: IFiltersCharts = {};

    for (const key in defaultFilter) {
      if (defaultFilter.hasOwnProperty(key)) {
        // @ts-ignore
        if (JSON.stringify(defaultFilter[key]) !== JSON.stringify(data[key])) {
          // @ts-ignore
          changes[key] = data[key];
        }
      }
    }

    for (const key in data) {
      if (data.hasOwnProperty(key) && !defaultFilter.hasOwnProperty(key)) {
        // @ts-ignore
        changes[key] = data[key];
      }
    }
    setIsChangeData(Object.keys(changes).length > 0);
    dispatch(
      reducerChangeDataCharts({
        hasChanged: Object.keys(changes).length > 0,
        changes,
      }),
    );
  };

  const handleClickSetting = () => {
    if (isOpenSetting) {
      setIsOpenSectionCharts(false);
    } else {
      getChangeData(facingChartsFilters);
    }
    setIsOpenSetting(!isOpenSetting);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (listRef.current && !listRef.current.contains(e.target as Node)) {
      setIsOpenSetting(false);
      setIsOpenSectionCharts(false);
    }
  };

  const handleClickOnLegend = (name: string) => {
    if (!isHiddenLabel.includes(name)) {
      setIsHiddenLabel((prevItems: string[]) => {
        return [...prevItems, name];
      });
    } else {
      setIsHiddenLabel((prevItems: string[]) => {
        return prevItems.filter((item) => item !== name);
      });
    }
  };

  const handleClickFilterOnType = (name: string, index: number) => {
    dispatch(reducerEditTypeChart({ id, pageId, data: name }));
    setIsHiddenLabel([]);
  };

  useEffect(() => {
    if (facingChartsFilters) {
      getChangeData(facingChartsFilters);
    }
    // eslint-disable-next-line
  }, [facingChartsFilters]);

  useEffect(() => {
    if (isPeriod.length > 0) {
      const timeoutId = setTimeout(() => {
        const updatedData = dataFacingCharts
          ? normList.includes(type)
            ? dataPrecent
            : dataFacingCharts
          : [];
        const directionData = reverseChartsList.includes(type)
          ? updatedData.slice().reverse()
          : updatedData;
        setIsVisiblelabel(filteredZeroData(updatedData));
        setIsData(directionData);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line
  }, [isPeriod, normList, type]);

  useEffect(() => {
    if (facingChartsFilters?.group?.value === '0') {
      const period = periodListOfDate.map((el: string) => {
        return axisX[facingChartsFilters?.group?.value].formatDate(el);
      });
      setIsPeriod(period);
    } else {
      const period =
        facingChartsFilters && periodList && periodList.length > 0
          ? periodList.map((el: string) => {
              if (facingChartsFilters?.group?.value === '7') {
                const parts = el.split('W');
                return parseInt(parts[1], 10);
              } else if (facingChartsFilters?.group?.value === '8') {
                return axisX[6].formatDate(el);
              } else if (
                facingChartsFilters?.group?.value > 8 &&
                facingChartsFilters?.group?.value < 15
              ) {
                return el || '';
              } else {
                return facingChartsFilters?.group?.value > 5
                  ? axisX[0].formatDate(el)
                  : axisX[facingChartsFilters?.group?.value].formatDate(el);
              }
            })
          : [];
      setIsPeriod(period);
    }
    // eslint-disable-next-line
  }, [periodList]);

  useEffect(() => {
    if (isOpenSetting) {
      const listEl = listRef.current;
      if (listEl) {
        const list = listEl.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        setIsListRightPosition(list.right + 168 > viewportWidth);
        setIsListRightFiltersPosition(
          list.right + 470 > viewportWidth || viewportWidth < 1030,
        );
      }
    }
    // eslint-disable-next-line
  }, [isOpenSetting]);

  useEffect(() => {
    if (filters) {
      setIsloader(true);
      dispatch(actionsAnalyticFacing(id, pageId)).then(() => {
        setIsloader(false);
      });
    }
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    if (pending[pageId || 0]) {
      setIsloader(pending[pageId || 0]?.includes(id) ? true : false);
    }
    // eslint-disable-next-line
  }, [pending]);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      if (legendRef.current) {
        const currentScrollTop = legendRef.current.scrollTop;
        const newScrollTop =
          event.deltaY > 0
            ? currentScrollTop + scrollStep
            : currentScrollTop - scrollStep;

        legendRef.current.scrollTo({
          top: newScrollTop,
          behavior: 'smooth',
        });
      }
    };

    const legendElement = legendRef.current;

    if (legendElement) {
      legendElement.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (legendElement) {
        legendElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, [scrollStep, isloader]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div
      className={`content-charts content-charts_long ${
        isIncreasedSize ? 'content-charts_active' : ''
      }`}
    >
      <div className='charts__header'>
        <p className='charts__title'>{`Фейсинг ${isCopy ? '(дубль)' : ''}`}</p>
        <div className='charts__btn'>
          <div>
            {!isIncreasedSize && (
              <IncreaseIcon
                className='charts__icon'
                onClick={() => setIsIncreasedSize(true)}
              />
            )}
            {isIncreasedSize && (
              <ReduceIcon
                className='charts__icon'
                onClick={() => setIsIncreasedSize(false)}
              />
            )}
          </div>
          <div className='charts__headerbtn'>
            <HeaderBtn
              content=' '
              purpose='filtersCharts'
              modalTitle='Настройка отображения данных'
              selectId={id}
              action={actionsAnalyticFacing}
              inputValuesCharts={facingChartsFilters}
            />
            {(!isChangeData || !facingChartsFilters) && (
              <FiltersIcon
                className={`charts__icon charts__icon_filters`}
                onClick={handleClickSetting}
              />
            )}
            {isChangeData && facingChartsFilters && (
              <ActiveFiltersIcon
                className={`charts__icon charts__icon_filters`}
                onClick={handleClickSetting}
              />
            )}
          </div>
          <div ref={listRef}>
            <SettingIcon
              className='charts__icon'
              onClick={handleClickSetting}
            />
            {isOpenSetting && (
              <ul
                className={`charts__setting ${
                  isListRightPosition ? 'charts__setting_right' : ''
                }`}
              >
                <li
                  onClick={() => {
                    handleClickSetting();
                    copyChart(id);
                  }}
                >
                  Дублировать
                </li>
                <li>
                  <HeaderBtn
                    content='Удалить'
                    purpose='removeCharts'
                    modalTitle='Удалить диаграмму?'
                    selectId={id}
                  />
                </li>
                <li
                  style={{ position: 'relative' }}
                  onClick={() => {
                    setIsOpenSectionCharts(!isOpenSectionCharts);
                  }}
                >
                  Тип диаграммы <ArrowIcon className='charts__icon' />
                  {isOpenSectionCharts && (
                    <ul
                      className={`charts__setting charts__setting_list ${
                        isListRightFiltersPosition
                          ? 'charts__setting_list_typeRight'
                          : ''
                      }`}
                    >
                      {typesCharts.map(
                        (
                          el: { value: string; label: string },
                          index: number,
                        ) => (
                          <li
                            key={index}
                            className={type === el.value ? 'item_active' : ''}
                            onClick={() =>
                              handleClickFilterOnType(el.value, index)
                            }
                          >
                            {el.label}
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                </li>
                <li>
                  <HeaderBtn
                    content='Скриншот'
                    purpose='screenCharts'
                    modalTitle='Сделать скриншот'
                    dataCharts={{
                      data,
                      name: `Фейсинг ${isCopy ? '(дубль)' : ''}`,
                      type: type,
                      options: actualOptionsChart,
                      isIncreasedSize,
                      labels,
                      isHiddenItem: isHiddenLabel,
                      colorOptions: colorList,
                      chartType: 'facing',
                    }}
                  />
                </li>
                <li>
                  <HeaderBtn
                    content='Настройка цвета'
                    purpose='changeColor'
                    modalTitle='Настройка цвета'
                    dataCharts={{ type: 'facing', labels, isVisiblelabel }}
                  />
                </li>
                {/* <li onClick={handleClickSetting}>Выгрузить данные</li> */}
              </ul>
            )}
          </div>
        </div>
      </div>
      {isloader ? (
        <ChartLoader />
      ) : dataFacingCharts?.length === 0 ? (
        <p className='charts__text'>
          Данные по фейсам в выбранных вами отчётах не найдены. Убедитесь, что
          вы заказали эту услугу, расширьте рассматриваемый период или
          географический охват.
        </p>
      ) : (
        <>
          <ChartComponent
            type={type}
            data={data}
            isHiddenItem={isHiddenLabel}
            options={actualOptionsChart}
            isIncreasedSize={isIncreasedSize}
            loader={isloader}
            chartType={'facing'}
            colorOptions={colorList}
          />
          {groupped !== 'skuName' && (
            <p className='charts__notice'>{`Вы выбрали очень много позиций для вывода на одной диаграмме, поэтому данные были сгруппированы по 
        ${grouppedName}. Если вы хотите получить данные по отдельным SKU, выберите их в фильтрах.`}</p>
          )}
          <ScrollingLegend
            colorOptions={colorList}
            handleClickOnLegend={handleClickOnLegend}
            isIncreasedSize={isIncreasedSize}
            isVisiblelabel={isVisiblelabel}
            isHiddenLabel={isHiddenLabel}
            isLabels={labels}
            type={type}
          />
        </>
      )}
    </div>
  );
};

export default FacingChart;
